import {
  deleteV1CompaniesCompanyIdFastWeighConnectionsId,
  FastWeigh_Connection_Create,
  FastWeigh_Connection_Update,
  getV1CompaniesCompanyIdFastWeighConnections,
  patchV1CompaniesCompanyIdFastWeighConnectionsId,
  postV1CompaniesCompanyIdFastWeighConnections,
  putV1CompaniesCompanyIdFastWeighConnectionsIdTestConnection,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { UNPROCESSABLE_ENTITY } from '~constants/errorMessagesConsts';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FastWeighConnection, FastWeighConnectionTest } from './models';

// The three independently-togglable flows, as the PATCH body keys that carry them.
export type FastWeighFlowKey = keyof Pick<
  FastWeigh_Connection_Update,
  'order_push_enabled' | 'project_push_enabled' | 'ticket_pull_enabled'
>;

export default function useFastWeigh() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const createConnection = useCallback(
    async (body: FastWeigh_Connection_Create) => {
      try {
        const response = await postV1CompaniesCompanyIdFastWeighConnections({
          body,
          path: { 'company-id': companyId },
        });

        return FastWeighConnection.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_ENTITY) {
          throw error;
        }

        try {
          connection.handleRequestError(
            error,
            t('error_messages.fast_weigh.failed_to_create_connection'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [companyId],
  );

  const deleteConnection = useCallback(
    async (id: string) => {
      try {
        await deleteV1CompaniesCompanyIdFastWeighConnectionsId({
          path: { 'company-id': companyId, id },
        });

        return true;
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.fast_weigh.failed_to_delete_connection'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return false;
      }
    },
    [companyId],
  );

  const listConnections = useCallback(async () => {
    try {
      const response = await getV1CompaniesCompanyIdFastWeighConnections({
        path: { 'company-id': companyId },
      });

      return response.data.data.map(FastWeighConnection.parse);
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.fast_weigh.failed_to_fetch_connections'),
        );
      } catch {
        // Error handling side effects already executed in connection.handleRequestError.
      }

      return null;
    }
  }, [companyId]);

  const testConnection = useCallback(
    async (id: string) => {
      try {
        const response =
          await putV1CompaniesCompanyIdFastWeighConnectionsIdTestConnection({
            path: { 'company-id': companyId, id },
          });

        return FastWeighConnectionTest.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.fast_weigh.failed_to_test_connection'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [companyId],
  );

  const updateConnection = useCallback(
    async (id: string, body: FastWeigh_Connection_Update) => {
      try {
        const response = await patchV1CompaniesCompanyIdFastWeighConnectionsId({
          body,
          path: { 'company-id': companyId, id },
        });

        return FastWeighConnection.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_ENTITY) {
          throw error;
        }

        try {
          connection.handleRequestError(
            error,
            t('error_messages.fast_weigh.failed_to_update_connection'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [companyId],
  );

  return {
    createConnection,
    deleteConnection,
    listConnections,
    testConnection,
    updateConnection,
  };
}
